import {
  CompetencePassport,
  LearningOutcome,
  Level,
} from "@/server/backend-api/generated";
import {
  CompetenceBaseTreeEx,
  CompetenceBaseWithChildrenEx,
} from "@/server/backend-api";
import {
  CompetenceRelationshipMap,
  getFlatCompetencesWithoutRoot,
  getTopicsWithAllSubtopics,
} from "@/utils/competenceTree";
import { Assert } from "@/utils/assert";
import { useMemo } from "react";
import {
  MotivationScanAnswers,
  MotivationScanAnswersWithStats,
} from "@/utils/motivationScan";
import { GraphSubtopic } from "@/components/CompetenceGraph/CompetenceGraph";

type CompletedLOsByLevel = {
  [level in Level]: number;
};

export class CompetencePassportWithStats {
  _flatCompetences: CompetenceBaseWithChildrenEx[];
  _subtopicIdToPotentialCompletedLOs: Map<string, CompletedLOsByLevel> =
    new Map();
  _subtopicIdToCompletedLOs: Map<string, CompletedLOsByLevel> = new Map();
  _relationshipMap: CompetenceRelationshipMap;
  _answers: MotivationScanAnswersWithStats;

  constructor(
    tree: CompetenceBaseTreeEx,
    outcomes: LearningOutcome[],
    currentCompetences: CompetencePassport | null,
    answers: MotivationScanAnswers | null
  ) {
    this._flatCompetences = getFlatCompetencesWithoutRoot(tree);
    this._relationshipMap = new CompetenceRelationshipMap(tree);
    this._answers = new MotivationScanAnswersWithStats(
      answers,
      this._relationshipMap
    );
    const topics = getTopicsWithAllSubtopics(tree);

    function _init(map: Map<string, CompletedLOsByLevel>) {
      for (const topic of topics) {
        for (const subtopic of topic.children) {
          map.set(subtopic.id, {
            [Level.LevelAExplorer]: 0,
            [Level.LevelBExpert]: 0,
            [Level.LevelCPioneer]: 0,
          });
        }
      }
    }

    // Populate completed LOs.
    _init(this._subtopicIdToCompletedLOs);
    if (currentCompetences !== null) {
      for (const topic of currentCompetences.topics) {
        for (const subtopic of topic.competencePassportSubTopicWithNoOfCompletedLearningOpportunitiesByLevel) {
          const completedLOs = this._subtopicIdToCompletedLOs.get(subtopic.id);
          Assert.check(
            completedLOs !== undefined,
            "Subtopic should be set in constructor."
          );
          completedLOs[Level.LevelAExplorer] +=
            subtopic.noOfCompletedLearningOpportunitiesExplorer;
          completedLOs[Level.LevelBExpert] +=
            subtopic.noOfCompletedLearningOpportunitiesExpert;
          completedLOs[Level.LevelCPioneer] +=
            subtopic.noOfCompletedLearningOpportunitiesPioneer;
          this._subtopicIdToCompletedLOs.set(subtopic.id, completedLOs);
        }
      }
    }

    // Initialize potential completed LOs to copy of completed LOs, since completed LOs should be included.
    for (const [key, value] of this._subtopicIdToCompletedLOs) {
      this._subtopicIdToPotentialCompletedLOs.set(key, { ...value });
    }

    // Populate potential completed LOs.
    for (const outcome of outcomes) {
      if (outcome.competenceAndLevels === null) {
        continue;
      }
      for (const competenceAndLevel of outcome.competenceAndLevels) {
        Assert.check(
          competenceAndLevel.competenceId !== null,
          "competenceId is null"
        );
        Assert.check(
          competenceAndLevel.level !== null,
          "Level is required here."
        );
        const subtopicId = this._relationshipMap
          .getRelationshipChain(competenceAndLevel.competenceId)
          .reverse()[1]; // Topic index is now 0, so subtopic is at index 1.

        const potentialCompletedLOs =
          this._subtopicIdToPotentialCompletedLOs.get(subtopicId);
        Assert.check(
          potentialCompletedLOs !== undefined,
          "completedLOs should be set in constructor."
        );

        potentialCompletedLOs[competenceAndLevel.level]++;
        this._subtopicIdToPotentialCompletedLOs.set(
          subtopicId,
          potentialCompletedLOs
        );
      }
    }
  }

  getPotentialCompletedLOs(subtopicId: string): CompletedLOsByLevel {
    const completedLOs =
      this._subtopicIdToPotentialCompletedLOs.get(subtopicId);
    Assert.check(completedLOs !== undefined, "Unrecognized subtopicId.");
    return completedLOs;
  }

  getCompletedLOs(subtopicId: string): CompletedLOsByLevel {
    const completedLOs = this._subtopicIdToCompletedLOs.get(subtopicId);
    Assert.check(completedLOs !== undefined, "Unrecognized subtopicId.");
    return completedLOs;
  }

  _mapTopicToCompetenceGraph(
    topicId: string,
    subtopicIdToCompletedLOs: Map<string, CompletedLOsByLevel>
  ): GraphSubtopic[] {
    const results: GraphSubtopic[] = [];
    const subtopicIds = this._relationshipMap.getChildren(topicId);
    for (const subtopicId of subtopicIds) {
      const subtopic = this._flatCompetences.find((c) => c.id === subtopicId);
      Assert.check(subtopic !== undefined, "Subtopic should be set.");
      const subtopicName = subtopic.name;
      Assert.notNull(subtopicName, "Subtopic name should be set.");

      const completedLOs = subtopicIdToCompletedLOs.get(subtopicId);
      Assert.check(completedLOs !== undefined, "Unrecognized subtopicId.");
      const isLiked = this._answers.isLiked(subtopicId);
      const result: GraphSubtopic = {
        name: subtopicName,
        isMotivated: isLiked,
        completedLOsByLevel: {
          [Level.LevelAExplorer]: completedLOs[Level.LevelAExplorer],
          [Level.LevelBExpert]: completedLOs[Level.LevelBExpert],
          [Level.LevelCPioneer]: completedLOs[Level.LevelCPioneer],
        },
        url:`/topics/${topicId}/#${subtopicId}`,
        id:subtopicId,
      };
      results.push(result);
    }
    return results;
  }

  mapTopicToCompletedCompetenceGraph(topicId: string): GraphSubtopic[] {
    return this._mapTopicToCompetenceGraph(
      topicId,
      this._subtopicIdToCompletedLOs
    );
  }

  mapTopicToPotentialCompletedCompetenceGraph(
    topicId: string
  ): GraphSubtopic[] {
    return this._mapTopicToCompetenceGraph(
      topicId,
      this._subtopicIdToPotentialCompletedLOs
    );
  }
}

export function useCompetencePassportStats(
  tree: CompetenceBaseTreeEx,
  outcomes: LearningOutcome[],
  currentCompetences: CompetencePassport | null,
  answers: MotivationScanAnswers | null
) {
  return useMemo(
    () =>
      new CompetencePassportWithStats(
        tree,
        outcomes,
        currentCompetences,
        answers
      ),
    [tree, outcomes, currentCompetences, answers]
  );
}
